import React, { useState } from "react";
import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type StatusValue =
  | "scheduled"
  | "completed"
  | "canceled"
  | "cancelled w/ fee"
  | "in progress"
  | "follow-up"
  | "follow-up scheduled"
  | "follow-up completed";

interface StatusSelectProps {
  value: StatusValue;
  onChange: (value: StatusValue) => void;
  autoOpen?: boolean;
  className?: string;
  onBlur?: () => void;
}

const options: { label: string; value: StatusValue }[] = [
  { label: "Scheduled", value: "scheduled" },
  { label: "Completed", value: "completed" },
  { label: "Canceled", value: "canceled" },
  { label: "Cancelled w/ fee", value: "cancelled w/ fee" },
  { label: "In Progress", value: "in progress" },
  { label: "Follow-up Scheduled", value: "follow-up scheduled" },
  { label: "Follow-up Completed", value: "follow-up completed" },
];

const StatusSelect: React.FC<StatusSelectProps> = ({
  value,
  onChange,
  autoOpen = false,
  className,
  onBlur,
}) => {
  const [open, setOpen] = useState(autoOpen);

  return (
    <Select.Root
      value={value}
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        // If dropdown is closing and onBlur is provided, call it
        if (!isOpen && onBlur) {
          onBlur();
        }
      }}
      onValueChange={(val) => {
        onChange(val as StatusValue);
        setOpen(false);
      }}
    >
      <Select.Trigger
        className={cn(
          "flex h-9 w-full items-center justify-between rounded border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
      >
        <Select.Value />
        <Select.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Content
        className="z-50 rounded border border-border bg-popover text-popover-foreground shadow-lg dark:border-gray-600"
        side="bottom"
        align="start"
      >
        <Select.Viewport className="p-1">
          {options.map((opt) => (
            <Select.Item
              key={opt.value}
              value={opt.value}
              className="flex cursor-pointer select-none items-center rounded px-3 py-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[state=checked]:font-semibold data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
            >
              <Select.ItemText>{opt.label}</Select.ItemText>
              <Select.ItemIndicator className="ml-auto text-green-600 dark:text-green-400">
                <Check className="h-4 w-4" />
              </Select.ItemIndicator>
            </Select.Item>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Root>
  );
};

export default StatusSelect;
